import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';

export default function BuildingFiberPage() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const services = [
    { title: 'Riser Kablo Çekimi', desc: 'Binanın ana omurgasını oluşturan dikey fiber kablo altyapısı', icon: 'ri-building-line', features: ['Dikey Kablo Yolu', 'Yangın Bariyeri', 'Kablo Sabitleme', 'Etiketleme'] },
    { title: 'Kat Dağıtımı', desc: 'Her kata fiber dağıtımı ve daire içi sonlandırma', icon: 'ri-stack-line', features: ['Kat ODF', 'Daire Sonlandırma', 'Patch Panel', 'Test ve Devreye Alma'] },
    { title: 'ODF Kurulumu', desc: 'Merkezi ve kat ODF kurulumu, fiber yönetimi', icon: 'ri-server-line', features: ['Merkezi ODF', 'Kat ODF', 'Splice Tray', 'Kablo Yönetimi'] },
    { title: 'Altyapı Testi', desc: 'OTDR test, güç ölçümü ve sertifikasyon', icon: 'ri-line-chart-line', features: ['OTDR Test', 'Güç Ölçümü', 'Raporlama', 'Sertifikasyon'] },
  ];

  const buildingTypes = [
    { type: 'Konut Binaları', desc: 'Apartman ve site içi fiber altyapı kurulumu', icon: 'ri-home-4-line', details: ['FTTH Çözümleri', 'Daire Sonlandırma', 'Ortak Alan Ağı', 'Yönetim Paneli'] },
    { type: 'İş Merkezleri', desc: 'Ofis binalarında yüksek kapasiteli fiber altyapı', icon: 'ri-building-2-line', details: ['Yüksek Kapasite', 'Kat Dağıtımı', 'Patch Panel', 'Redundant Altyapı'] },
    { type: 'Alışveriş Merkezleri', desc: 'AVM ve ticari alanlarda fiber network altyapısı', icon: 'ri-store-2-line', details: ['Mağaza Bağlantısı', 'Merkezi Yönetim', 'Güvenlik Ağı', 'WiFi Altyapısı'] },
    { type: 'Otel ve Rezidans', desc: 'Otel ve rezidans projelerinde fiber çözümler', icon: 'ri-hotel-line', details: ['Oda Bağlantısı', 'IPTV Altyapısı', 'Misafir Ağı', 'Yönetim Sistemi'] },
  ];

  const faqData = [
    { question: 'Bina fiber altyapı kurulumu ne kadar sürer?', answer: 'Bina fiber altyapı kurulum süresi, bina büyüklüğü ve kat sayısına bağlıdır. 10 katlı standart bir apartman için ortalama 5-7 iş günü sürmektedir.' },
    { question: 'Riser kablo nedir ve neden önemlidir?', answer: 'Riser kablo, binanın dikey omurgasını oluşturan ana fiber kablodur. Bodrum veya zemin kattan çatıya kadar uzanır ve her kata fiber dağıtımı sağlar.' },
    { question: 'FTTH (Fiber to the Home) nedir?', answer: 'FTTH, fiber optik kablonun doğrudan kullanıcının evine kadar ulaştırılması anlamına gelir. Bu teknoloji ile gigabit hızlarda internet erişimi sağlanır.' },
    { question: 'Mevcut binaya fiber altyapı eklenebilir mi?', answer: 'Evet, mevcut binalara da fiber altyapı eklenebilir. Binanın mevcut kablo yolları değerlendirilir, gerekirse yeni kablo yolları oluşturulur.' },
    { question: 'Bina fiber altyapısı için garanti veriyor musunuz?', answer: 'Evet, tüm bina fiber altyapı projelerimiz için 5 yıl sistem garantisi veriyoruz.' },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } }))
  };

  return (
    <>
      <title>Bina Fiber Altyapı Kurulumu İstanbul | FTTH Fiber Optik Altyapı</title>
      <meta name="description" content="İstanbul ve çevre illerde konut ve iş merkezlerinde profesyonel fiber optik altyapı kurulumu. FTTH çözümleri, riser kablo çekimi, kat dağıtımı, ODF kurulumu ve test hizmetleri." />
      <meta name="keywords" content="bina fiber altyapı, FTTH, fiber to the home, riser kablo, kat dağıtımı, ODF kurulum, apartman fiber, iş merkezi fiber, İstanbul fiber altyapı" />
      <link rel="canonical" href="https://fiberek.com/fiber-hizmetleri/bina-fiber-altyapi" />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-dark-950">
        <Navbar />

        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-900/20 via-dark-950 to-purple-900/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  Bina Fiber Optik
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">Altyapı Kurulumu</span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">Konut ve iş merkezlerinde profesyonel fiber optik altyapı kurulumu. FTTH çözümleri, riser kablo çekimi, kat dağıtımı, ODF kurulumu ve kapsamlı test hizmetleri.</p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <a href="tel:+905537165561" className="inline-flex items-center gap-2 px-8 py-4 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-all duration-300 font-semibold shadow-lg shadow-violet-500/30 whitespace-nowrap cursor-pointer"><i className="ri-phone-line"></i>Hemen Ara</a>
                  <a href="https://wa.me/908505551234" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"><i className="ri-whatsapp-line"></i>WhatsApp</a>
                </div>
                <div className="grid grid-cols-3 gap-6">
                  <div><div className="text-3xl font-bold text-violet-400 mb-1">500+</div><div className="text-sm text-gray-400">Tamamlanan Proje</div></div>
                  <div><div className="text-3xl font-bold text-purple-400 mb-1">5 Yıl</div><div className="text-sm text-gray-400">Sistem Garantisi</div></div>
                  <div><div className="text-3xl font-bold text-fuchsia-400 mb-1">1 Gbps</div><div className="text-sm text-gray-400">Hız Kapasitesi</div></div>
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
                <div className="relative w-full h-96 rounded-2xl overflow-hidden">
                  <img src="https://public.readdy.ai/ai/img_res/3812a0ceffa6bee4308f706d562eec46.jpg" alt="Bina Fiber Altyapı" className="w-full h-full object-cover" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16"><h2 className="text-4xl font-bold text-white mb-4">Bina Fiber Altyapı Hizmetlerimiz</h2></div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <motion.div key={service.title} ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: index * 0.1 }} className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-6 border border-white/10 hover:border-violet-500/50 transition-all duration-300">
                  <div className="w-14 h-14 bg-violet-500/20 rounded-lg flex items-center justify-center mb-4"><i className={`${service.icon} text-2xl text-violet-400`}></i></div>
                  <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{service.desc}</p>
                  <ul className="space-y-1">{service.features.map((feature) => (<li key={feature} className="flex items-center gap-2 text-xs text-gray-300"><i className="ri-check-line text-violet-400"></i>{feature}</li>))}</ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-violet-900/10 via-dark-950 to-purple-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16"><h2 className="text-4xl font-bold text-white mb-4">Hizmet Verdiğimiz Bina Tipleri</h2></div>
            <div className="grid md:grid-cols-2 gap-8">
              {buildingTypes.map((building, index) => (
                <motion.div key={building.type} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: index * 0.1 }} className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-8 border border-white/10">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 bg-violet-500/20 rounded-xl flex items-center justify-center flex-shrink-0"><i className={`${building.icon} text-3xl text-violet-400`}></i></div>
                    <div><h3 className="text-2xl font-bold text-white mb-2">{building.type}</h3><p className="text-gray-400">{building.desc}</p></div>
                  </div>
                  <ul className="grid grid-cols-2 gap-3">{building.details.map((detail) => (<li key={detail} className="flex items-center gap-2 text-sm text-gray-300"><i className="ri-check-line text-violet-400"></i>{detail}</li>))}</ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-violet-900/10 via-dark-950 to-purple-900/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><h2 className="text-4xl font-bold text-white mb-4">Sıkça Sorulan Sorular</h2></div>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <motion.details key={index} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: index * 0.1 }} className="group bg-dark-900 rounded-xl border border-white/10 overflow-hidden">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-2xl text-violet-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-6"><p className="text-gray-400 leading-relaxed">{faq.answer}</p></div>
                </motion.details>
              ))}
            </div>
          </div>
        </section>

        {/* Blog & Rehberler Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Blog & Rehberler</h2>
              <p className="text-gray-400">Fiber optik teknolojileri hakkında detaylı bilgiler</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-violet-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20building%20fiber%20optic%20infrastructure%20installation%2C%20technician%20installing%20fiber%20cables%20in%20vertical%20riser%20shaft%2C%20organized%20fiber%20patch%20panels%20and%20ODF%20frames%20in%20building%20telecom%20room&width=400&height=250&seq=building-blog1&orientation=landscape"
                    alt="Fabrikalarda Fiber Altyapı"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-violet-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fabrikalarda Fiber Altyapı Rehberi</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Endüstriyel tesislerde fiber optik altyapı planlama, kurulum ve yönetim süreçleri hakkında kapsamlı rehber.</p>
                  <a
                    href="/blog/fabrikalarda-fiber-altyapi-rehberi"
                    className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-violet-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fusion%20splicing%20fiber%20optic%20process%2C%20close-up%20view%20of%20fiber%20fusion%20splicer%20machine%20in%20operation%2C%20precise%20fiber%20alignment%20and%20welding%2C%20professional%20technical%20environment&width=400&height=250&seq=building-blog2&orientation=landscape"
                    alt="Fusion Splicing"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-violet-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fusion Splicing Nedir? Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik kabloların kaynaştırılarak birleştirilmesi işlemi olan fusion splicing tekniği ve uygulama detayları.</p>
                  <a
                    href="/blog/fusion-splicing-nedir"
                    className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-violet-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=OTDR%20fiber%20optic%20testing%20equipment%20display%20showing%20measurement%20results%2C%20professional%20technician%20analyzing%20fiber%20test%20data%2C%20modern%20testing%20laboratory%20with%20technical%20instruments&width=400&height=250&seq=building-blog3&orientation=landscape"
                    alt="OTDR Test"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-violet-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">OTDR Testi Nedir ve Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik hatların kalite kontrolü ve arıza tespiti için kullanılan OTDR test yöntemleri ve uygulama aşamaları.</p>
                  <a
                    href="/blog/otdr-testi-nedir"
                    className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Bina Fiber Altyapı Projesi İçin Teklif Alın</h2>
            <p className="text-xl text-gray-300 mb-8">Ücretsiz keşif ve detaylı proje planlaması</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="tel:+905537165561" className="inline-flex items-center gap-2 px-8 py-4 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-violet-500/30 whitespace-nowrap cursor-pointer"><i className="ri-phone-line"></i>+90 553 716 55 61</a>
              <a href="https://wa.me/905537165561" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"><i className="ri-whatsapp-line"></i>WhatsApp</a>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingButtons />
      </div>
    </>
  );
}
